import {
  LockOutlined,
  SearchOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { Modal, Space, Spin, Switch, Tooltip, Typography } from 'antd';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ScopeButton } from '../components/buttons';
import { RoundInput } from '../components/formInputs';
import { IconQuestionMark } from './icons';
import { ColoredTag } from './tags';
import {
  AdvancedUserRightsCodes,
  TablePermission,
  type AdvancedUserRights,
  type TablePermissionValue,
  type UserRights,
} from './types';

const ACCESS_COLUMN_WIDTH_PX = 96;
const ENTITY_COLUMN_WIDTH_PX = 200;
const LEVEL_GRID_COLUMNS = 'minmax(160px, 1fr) 120px 48px 132px 112px';
const LEVEL_OPERATIONS_COLUMN = '148px';

type LevelSelection = {
  refresh: boolean;
  read: boolean;
  write: boolean;
};

const emptyLevel = (): LevelSelection => ({
  refresh: false,
  read: false,
  write: false,
});

const selectionFromPermission = (permission: string | undefined): LevelSelection => {
  switch (permission) {
    case TablePermission.REFRESH:
      return { refresh: true, read: false, write: false };
    case TablePermission.READ:
      return { refresh: false, read: true, write: false };
    case TablePermission.WRITE:
      return { refresh: false, read: false, write: true };
    case TablePermission.ALL:
      return { refresh: false, read: true, write: true };
    default:
      return emptyLevel();
  }
};

const permissionFromSelection = (selection: LevelSelection): TablePermissionValue => {
  if (selection.read && selection.write) return TablePermission.ALL;
  if (selection.write) return TablePermission.WRITE;
  if (selection.read) return TablePermission.READ;
  if (selection.refresh) return TablePermission.REFRESH;
  return TablePermission.NONE;
};

type AccessToggle = 'refresh' | 'read' | 'write';

const applyLevelToggle = (
  selection: LevelSelection,
  toggle: AccessToggle,
  enabled: boolean,
): LevelSelection => {
  if (toggle === 'refresh') {
    return enabled
      ? { refresh: true, read: false, write: false }
      : { ...selection, refresh: false };
  }
  if (toggle === 'read') {
    return { refresh: false, read: enabled, write: selection.write };
  }
  return { refresh: false, read: selection.read, write: enabled };
};

const RightsSpin = styled(Spin)`
  /* Root is .ant-spin-nested-loading — fill the drawer pane. */
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  > .ant-spin-container {
    display: flex;
    flex-direction: column;
    flex: 1 1 0%;
    min-height: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

const StyledSwitch = styled(Switch)`
  &:not(.ant-switch-small) {
    min-width: 44px;
  }

  &.ant-switch-small {
    min-width: 28px;
  }

  background: #ccd4d4 !important;

  &:hover:not(.ant-switch-disabled) {
    background: var(--color-text-muted, #b3c0c0) !important;
  }

  &.ant-switch-checked {
    background: var(--color-primary, #41aaaf) !important;

    &:hover:not(.ant-switch-disabled) {
      background: var(--color-primary-hover, #64bbbd) !important;
    }
  }

  .ant-switch-inner {
    background: transparent !important;
  }

  .ant-switch-handle::before {
    background: var(--color-surface-raised, #ffffff) !important;
    box-shadow: 0 1px 4px rgba(4, 42, 42, 0.2);
  }
`;

const LevelColumn = styled.div<{ $area: 'refresh' | 'read' | 'write' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;

  @media (max-width: 767px) {
    grid-area: ${({ $area }) => $area};
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    padding-inline: 2px;
  }
`;

const LevelTitle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  color: var(--color-text-dark, #042a2a);
  white-space: nowrap;
`;

const LevelTitleLines = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;

  > span {
    white-space: nowrap;
  }
`;

const LevelHelpButton = styled.button`
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  color: var(--color-text-muted, #5a6a6a);
  border-radius: 50%;

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
  }
`;

const LevelSwitchLabel = styled.span`
  display: none;

  @media (max-width: 767px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    color: var(--color-text-dark, #042a2a);
  }
`;

const OperationsTestCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;

  @media (max-width: 767px) {
    grid-area: ops;
    justify-content: stretch;
  }
`;

const LevelOr = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-text-muted, #5a6a6a);
  white-space: nowrap;

  @media (max-width: 767px) {
    grid-area: or;
  }
`;

const LevelOrLabel = styled.span<{ $placement: 'header' | 'row' }>`
  display: ${({ $placement }) => ($placement === 'header' ? 'inline' : 'none')};

  @media (max-width: 767px) {
    display: ${({ $placement }) => ($placement === 'header' ? 'none' : 'inline')};
  }
`;

const RightsContent = styled.div`
  /* intro + search fixed rows; table/scroller takes the rest */
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 20px;
  width: 100%;
  min-height: 0;
  flex: 1 1 0%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 767px) {
    gap: 14px;
  }
`;

const RightsIntro = styled.p`
  display: block;
  margin: 0;
  max-width: 720px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: var(--color-text-muted, #5a6a6a);
  flex-shrink: 0;

  @media (max-width: 767px) {
    max-width: none;
    font-size: 14px;
    line-height: 22px;
    padding-inline: 2px;
    margin-bottom: 2px;
  }
`;

const RightsToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex-shrink: 0;

  @media (max-width: 767px) {
    gap: 10px;
    padding-inline: 2px;
    margin-bottom: 4px;

    .ant-input-affix-wrapper,
    input {
      max-width: none !important;
    }
  }
`;

const RightsTable = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const RightsScroller = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  /* Scrollable, but no visible scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* height:auto + max-height (set in layout effect) — does not rely on flex chain */
  height: auto;
  max-height: calc(100vh - 260px);
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px 48px 4px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  &.scroller-rights--levels {
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 767px) {
    padding: 4px 2px calc(32px + env(safe-area-inset-bottom, 0px));
    max-height: calc(100dvh - 220px);
  }
`;

const RightsGrid = styled.div<{
  $entityFirst?: boolean;
  $level?: boolean;
  $operations?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $entityFirst, $level, $operations }) =>
    $level
      ? `${LEVEL_GRID_COLUMNS}${
          $operations ? ` ${LEVEL_OPERATIONS_COLUMN}` : ''
        }`
      : $entityFirst
        ? `minmax(0, 1fr) ${ACCESS_COLUMN_WIDTH_PX}px`
        : `${ACCESS_COLUMN_WIDTH_PX}px ${ENTITY_COLUMN_WIDTH_PX}px minmax(0, 1fr)`};
  column-gap: ${({ $level }) => ($level ? '12px' : '24px')};
  align-items: ${({ $level }) => ($level ? 'center' : 'start')};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 767px) {
    grid-template-columns: ${({ $level }) =>
      $level ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto'};
    grid-template-areas: ${({ $entityFirst, $level, $operations }) =>
      $level
        ? `'entity' 'refresh' 'or' 'read' 'write'${$operations ? " 'ops'" : ''}`
        : $entityFirst
          ? `'entity access'`
          : `'entity access' 'perms perms'`};
    column-gap: 12px;
    row-gap: 8px;
    align-items: center;
  }
`;

const DrawerColumnHeader = styled.span`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;
  color: var(--color-text-dark, #042a2a);
`;

const RightsLevelHeader = styled.div<{ $operations?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $operations }) =>
    `${LEVEL_GRID_COLUMNS}${
      $operations ? ` ${LEVEL_OPERATIONS_COLUMN}` : ''
    }`};
  column-gap: 12px;
  row-gap: 8px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 2px 16px 14px;
  flex-shrink: 0;
  scrollbar-gutter: stable;

  .level-header-entity {
    grid-column: 1;
    grid-row: 1;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
  }

  .level-header-or {
    grid-column: 3;
    grid-row: 1;
    justify-self: center;
  }

  .level-header-title-refresh,
  .level-header-switch-refresh {
    grid-column: 2;
  }

  .level-header-title-read,
  .level-header-switch-read {
    grid-column: 4;
  }

  .level-header-title-write,
  .level-header-switch-write {
    grid-column: 5;
  }

  .level-header-title-refresh,
  .level-header-title-read,
  .level-header-title-write {
    grid-row: 1;
    justify-self: center;
  }

  .level-header-switch-refresh,
  .level-header-switch-read,
  .level-header-switch-write {
    grid-row: 2;
    justify-self: center;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const RightsTableHeader = styled(RightsGrid)`
  padding: ${({ $level }) => ($level ? '0 16px 4px' : '0 16px 8px 4px')};
  flex-shrink: 0;
  overflow-y: ${({ $level }) => ($level ? 'hidden' : 'visible')};
  scrollbar-gutter: ${({ $level }) => ($level ? 'stable' : 'auto')};

  @media (max-width: 767px) {
    padding: 8px 8px 12px;
    grid-template-areas: ${({ $level }) => ($level ? 'none' : `'entity access'`)};
    grid-template-columns: ${({ $level }) =>
      $level ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto'};
    row-gap: 0;
    border-bottom: ${({ $level }) =>
      $level ? 'none' : '1px solid var(--color-border, #dbeae3)'};
    margin: 0 2px 8px;
    display: ${({ $level }) => ($level ? 'none' : 'grid')};

    /* Hide desktop column titles */
    > ${DrawerColumnHeader} {
      display: none;
    }
  }
`;

const RightsDataRow = styled(RightsGrid)`
  padding: ${({ $level }) => ($level ? '10px 16px' : '4px 12px')};
  min-height: ${({ $level }) => ($level ? '56px' : '45px')};
  align-items: center;
  background: var(--color-surface, #f3f7f8);
  border-radius: 10px;
  margin-bottom: ${({ $level }) => ($level ? '8px' : '5px')};
  min-width: 0;
  box-sizing: border-box;
  transition: background 0.15s ease;

  &:hover {
    background: var(--color-row-hover, #e6eff2);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 767px) {
    padding: 14px;
    min-height: 0;
    margin-bottom: 10px;
    align-items: center;
  }
`;

const PermissionsPanel = styled.div<{ $compactEmpty?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  width: 100%;
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 52px;
  box-sizing: border-box;

  @media (max-width: 767px) {
    grid-area: perms;
    padding: 8px 10px;
    min-height: 0;
    gap: 8px;

    /* “None” / empty state — don’t waste a full card row */
    ${({ $compactEmpty }) =>
      $compactEmpty
        ? `
      display: none;
    `
        : ''}
  }
`;

const PermissionChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const PermissionChip = styled.button<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 6px 12px;
  border-radius: 6px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--color-primary, #41aaaf)' : 'var(--color-border, #dbeae3)'};
  background: ${({ $selected }) =>
    $selected
      ? 'var(--color-primary-soft, #e9f6f7)'
      : 'var(--color-surface-raised, #ffffff)'};
  color: ${({ $selected }) =>
    $selected
      ? 'var(--color-text-dark, #042a2a)'
      : 'var(--color-text-muted, #5a6a6a)'};

  &:hover:not(:disabled) {
    border-color: var(--color-primary, #41aaaf);
    color: var(--color-text-dark, #042a2a);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    padding: 8px 12px;
    min-height: 36px;
    font-size: 12px;
  }
`;

const QuestionMarkIcon = styled(IconQuestionMark)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--color-text-muted, #a9a9a9);
`;

const PermissionsFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;

  @media (max-width: 767px) {
    justify-content: stretch;

    > button,
    > .scope-button {
      width: 100% !important;
    }
  }
`;

const EntityNameCell = styled.div<{ $grow?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: ${({ $grow }) => ($grow ? '100%' : `${ENTITY_COLUMN_WIDTH_PX}px`)};
  max-width: ${({ $grow }) => ($grow ? 'none' : `${ENTITY_COLUMN_WIDTH_PX}px`)};
  min-width: 0;
  overflow: hidden;

  @media (max-width: 767px) {
    grid-area: entity;
    width: 100%;
    max-width: none;
    gap: 8px;

    .anticon,
    img {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      font-size: 22px;
      object-fit: contain;
    }
  }
`;

const AccessCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  @media (max-width: 767px) {
    grid-area: access;
    width: auto;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 0;
    flex-shrink: 0;

    /* Header grant-all: only the switch — label is MobileGrantAllLabel */
    &.user-rights-grant-all {
      > ${DrawerColumnHeader},
      .user-rights-grant-all__all-label {
        display: none;
      }
    }
  }
`;

const AccessStatus = styled.span<{ $active?: boolean }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  font-family: Inter, sans-serif;
  color: ${({ $active }) =>
    $active
      ? 'var(--color-text-dark, #042a2a)'
      : 'var(--color-text-muted, #5a6a6a)'};
  ${({ $active }) =>
    $active
      ? `
    padding: 2px 8px;
    border-radius: 4px;
    background: var(--color-primary-soft, #e9f6f7);
    border: 1px solid var(--color-primary, #41aaaf);
  `
      : `
    padding: 2px 0;
  `}

  @media (max-width: 767px) {
    /* Switch already shows on/off — badge is redundant and stretches the row */
    display: none;
  }
`;

const MobileGrantAllLabel = styled.span`
  display: none;

  @media (max-width: 767px) {
    display: block;
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    color: var(--color-text-dark, #042a2a);
    grid-area: entity;
    min-width: 0;
  }
`;

const LockedPermissionsHint = styled.button`
  display: none;
  appearance: none;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 767px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    grid-area: perms;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: var(--color-text-muted, #5a6a6a);
    padding: 0 2px;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const LockedDesktopPanel = styled(PermissionsPanel)`
  @media (max-width: 767px) {
    display: none;
  }
`;

const EntityNameText = styled(Typography.Text)`
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
  flex: 1;
  min-width: 0;
`;
type UserRightsPanelProps = {
  loading: boolean;
  rights?: UserRights[];
  saveUserRights: (userRight: UserRights, permission: TablePermissionValue) => void;
  saveAllUserRights: (
    userRights: UserRights[],
    permission: TablePermissionValue,
  ) => void;
  onClose: () => void;
  isAdvancedUserRights?: boolean;
  advancedUserRights?: AdvancedUserRights[];
  setAdvancedUserRight?: (
    accountUuid: string,
    organisationId: string,
    operationIds: string[],
  ) => void;
  setAdvancedUserRightToAll?: (
    accountUuid: string,
    organisationIds: string[],
    operationIds: string[],
  ) => void;
  onUpgradeClick?: () => void;
  renderProviderIcon?: (providerName: string) => ReactNode;
  /** Rows Users drawer: entity + access only. generate.TAX keeps the permissions column. */
  showPermissionsColumn?: boolean;
  /** Temporary: load operations for one entity row. Rows Users drawer only. */
  onLoadEntityOperations?: (accountId: string, organisationId: string) => void;
  /** Rows: set Refresh, Full Read, or Write on every entity for this staff member. */
  onToggleAllAccess?: (
    accountId: string,
    toggle: AccessToggle,
    enabled: boolean,
  ) => void;
  /**
   * Search chrome. Default keeps generate.TAX’s circular suffix.
   * `standard` is ACC-02: 32px field with a muted search icon, no filled button.
   */
  searchFieldVariant?: 'default' | 'standard';
};

const ADVANCED_LABEL_KEYS: Record<string, string> = {
  [AdvancedUserRightsCodes.CANCEL_SUBMISSION]: 'users.advanced-rights.cancel-submission',
  [AdvancedUserRightsCodes.CREATE_SUBMISSION]: 'users.advanced-rights.create-submission',
  [AdvancedUserRightsCodes.CREATE_EDIT_RETURN]: 'users.advanced-rights.create-edit-return',
  [AdvancedUserRightsCodes.VAT_NUMBER_CHECKER]: 'users.advanced-rights.vat-number-checker',
  [AdvancedUserRightsCodes.CREATE_TAX_RATES]: 'users.advanced-rights.create-tax-rates',
  [AdvancedUserRightsCodes.APPROVE_SUBMISSION]: 'users.advanced-rights.approve-submission',
  [AdvancedUserRightsCodes.GENERATE_RECONCILIATION]:
    'users.advanced-rights.generate-reconciliation',
};

const operationChipLabel = (
  right: AdvancedUserRights,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string => {
  const labelKey = ADVANCED_LABEL_KEYS[right.operationRightCode];
  const fromApi = right.name || right.shortName;
  return labelKey ? t(labelKey, { defaultValue: fromApi }) : fromApi;
};

export const UserRightsPanel = ({
  loading,
  rights,
  saveUserRights,
  saveAllUserRights,
  isAdvancedUserRights,
  advancedUserRights,
  setAdvancedUserRight,
  setAdvancedUserRightToAll,
  onUpgradeClick,
  renderProviderIcon,
  showPermissionsColumn = true,
  onLoadEntityOperations,
  onToggleAllAccess,
  searchFieldVariant = 'default',
}: UserRightsPanelProps) => {
  const { t } = useTranslation();
  const [access, setAccess] = useState<Record<string, boolean>>({});
  const [levels, setLevels] = useState<Record<string, LevelSelection>>({});
  const [advancedRightsSelection, setAdvancedRightsSelection] = useState<
    Record<string, string[]>
  >({});
  const [searchValue, setSearchValue] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [rightsList, setRightsList] = useState<UserRights[] | undefined>([]);
  const [applyToAllOrganisationId, setApplyToAllOrganisationId] = useState<
    string | null
  >(null);
  const initialised = useRef(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const defer = useCallback((cb: () => void) => {
    setTimeout(cb, 0);
  }, []);

  /* Bound the list to the drawer body so overflow-y scrolls even when the
     Ant/flex height chain fails to give the scroller a definite height. */
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const updateMaxHeight = () => {
      const clipParent =
        (scroller.closest('.ant-drawer-body') as HTMLElement | null) ??
        (scroller.closest('.drawer-content') as HTMLElement | null);
      if (!clipParent) {
        scroller.style.maxHeight = 'calc(100vh - 260px)';
        return;
      }
      const available = Math.floor(
        clipParent.getBoundingClientRect().bottom -
          scroller.getBoundingClientRect().top -
          12,
      );
      scroller.style.maxHeight =
        available > 80 ? `${available}px` : 'calc(100vh - 260px)';
    };

    updateMaxHeight();
    const ro = new ResizeObserver(updateMaxHeight);
    const clipParent =
      (scroller.closest('.ant-drawer-body') as HTMLElement | null) ??
      (scroller.closest('.drawer-content') as HTMLElement | null);
    if (clipParent) ro.observe(clipParent);
    window.addEventListener('resize', updateMaxHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, [loading, rightsList?.length]);

  const sortedAdvancedRights = useMemo(
    () => advancedUserRights?.slice().sort((a, b) => a.sortOrder - b.sortOrder) ?? [],
    [advancedUserRights],
  );

  useEffect(() => {
    if (!rights || rights.length === 0) {
      setAccess({});
      setLevels({});
      setRightsList(undefined);
      setAdvancedRightsSelection({});
      setSelectAll(false);
      initialised.current = false;
      return;
    }
    if (initialised.current) {
      setRightsList(rights);
      setLevels((prev) => {
        const next = { ...prev };
        rights.forEach((record) => {
          next[record.uuid] = selectionFromPermission(record.tablePermission);
        });
        return next;
      });
      return;
    }
    const defaultRights: Record<string, boolean> = {};
    const defaultLevels: Record<string, LevelSelection> = {};
    rights.forEach((r) => {
      defaultRights[r.uuid] = r.tablePermission !== TablePermission.NONE;
      defaultLevels[r.uuid] = selectionFromPermission(r.tablePermission);
    });
    setAccess(defaultRights);
    setLevels(defaultLevels);
    setRightsList(rights);
    setSelectAll(Object.values(defaultRights).every(Boolean));
    const initial: Record<string, string[]> = {};
    rights.forEach((r) => {
      initial[r.organisationId] = Array.isArray(r.advancedRights)
        ? [...r.advancedRights]
        : [];
    });
    setAdvancedRightsSelection(initial);
    initialised.current = true;
  }, [rights]);

  const handleSearch = () => {
    if (searchValue !== '') {
      setRightsList(
        rights?.filter((right) =>
          right?.friendlyName
            ?.toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase()),
        ),
      );
    } else {
      setRightsList(rights);
    }
  };

  const levelFor = (record: UserRights) =>
    levels[record.uuid] ?? selectionFromPermission(record.tablePermission);

  const allAccessOn = (toggle: AccessToggle) =>
    Boolean(rights?.length) && Boolean(rights?.every((record) => levelFor(record)[toggle]));

  const toggleAllAccess = (toggle: AccessToggle, enabled: boolean) => {
    if (!rights?.length || !onToggleAllAccess) return;
    const accountId = rights[0]?.accountUuid;
    if (!accountId) return;
    setLevels((prev) => {
      const next = { ...prev };
      rights.forEach((record) => {
        const current = next[record.uuid] ?? selectionFromPermission(record.tablePermission);
        next[record.uuid] = applyLevelToggle(current, toggle, enabled);
      });
      return next;
    });
    onToggleAllAccess(accountId, toggle, enabled);
  };

  const onSelectAll = () => {
    if (!rightsList) return;
    const newSelectAll = !selectAll;
    const previouslyActiveOrgIds: string[] = [];
    rightsList.forEach((rl) => {
      if (access[rl.uuid]) previouslyActiveOrgIds.push(rl.organisationId);
    });
    setSelectAll(newSelectAll);
    setAccess((prev) => {
      const next = { ...prev };
      rightsList.forEach((rl) => {
        next[rl.uuid] = newSelectAll;
      });
      return next;
    });
    defer(() =>
      saveAllUserRights(
        rightsList,
        newSelectAll ? TablePermission.ALL : TablePermission.NONE,
      ),
    );
    if (!newSelectAll && previouslyActiveOrgIds.length > 0 && setAdvancedUserRightToAll) {
      const accountUuid = rightsList[0]?.accountUuid;
      if (accountUuid) {
        defer(() => setAdvancedUserRightToAll(accountUuid, previouslyActiveOrgIds, []));
      }
    }
  };

  const handleApplyToAll = (organisationId: string) => {
    const selectedValues = advancedRightsSelection[organisationId] || [];
    const newSelections = { ...advancedRightsSelection };
    rightsList?.forEach((entity) => {
      if (access[entity.uuid]) {
        newSelections[entity.organisationId] = [...selectedValues];
      }
    });
    setAdvancedRightsSelection(newSelections);
    const activeOrganisationIds = Object.keys(newSelections).filter(
      (orgId) => newSelections[orgId].length > 0,
    );
    if (setAdvancedUserRightToAll && rightsList && rightsList.length > 0) {
      setAdvancedUserRightToAll(
        rightsList[0].accountUuid,
        activeOrganisationIds,
        selectedValues,
      );
    }
  };

  const handleAdvancedRightsChange = (
    accountUuid: string,
    rightOrganisationId: string,
    selectedIds: string[],
  ) => {
    const preparerRightsId = advancedUserRights?.find(
      (r) => r.operationRightCode === AdvancedUserRightsCodes.CREATE_SUBMISSION,
    )?.uuid;
    const reviewerRightsId = advancedUserRights?.find(
      (r) => r.operationRightCode === AdvancedUserRightsCodes.APPROVE_SUBMISSION,
    )?.uuid;
    const prevSelection = advancedRightsSelection[rightOrganisationId] || [];
    let nextIds = [...selectedIds];
    if (preparerRightsId && reviewerRightsId && nextIds.includes(reviewerRightsId)) {
      if (!nextIds.includes(preparerRightsId)) {
        if (!prevSelection.includes(preparerRightsId)) {
          nextIds.push(preparerRightsId);
        } else {
          nextIds = nextIds.filter((id) => id !== reviewerRightsId);
        }
      }
    }
    setAdvancedRightsSelection((prev) => ({
      ...prev,
      [rightOrganisationId]: nextIds,
    }));
    setAdvancedUserRight?.(accountUuid, rightOrganisationId, nextIds);
  };

  const activeEntityCount =
    rightsList?.filter((entity) => access?.[entity.uuid]).length ?? 0;
  const applyToAllSourceName = applyToAllOrganisationId
    ? rightsList?.find((r) => r.organisationId === applyToAllOrganisationId)
        ?.friendlyName || ''
    : '';

  return (
    <>
      <RightsSpin className="user-rights-panel" spinning={loading} size="large">
        <RightsContent className="user-rights-panel__content">
          <RightsIntro>{t('users.access-rights-description')}</RightsIntro>
          <RightsToolbar>
            <RoundInput
              className={
                searchFieldVariant === 'standard'
                  ? 'user-rights-search user-rights-search--standard'
                  : undefined
              }
              width="100%"
              height={searchFieldVariant === 'standard' ? '32px' : '45px'}
              size={searchFieldVariant === 'standard' ? 'middle' : 'large'}
              style={{
                flex: 1,
                maxWidth: showPermissionsColumn ? 420 : 480,
                minWidth: 0,
              }}
              value={searchValue}
              placeholder={`${t('common.search')} ${t('organisation.entities')}`}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setSearchValue(e.target.value);
                if (e.target.value === '') setRightsList(rights);
              }}
              onPressEnter={handleSearch}
              prefix={
                searchFieldVariant === 'standard' ? (
                  <SearchOutlined
                    className="user-rights-search__icon"
                    style={{
                      color: 'var(--color-icon-muted, #8a8796)',
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                    onClick={handleSearch}
                  />
                ) : undefined
              }
              suffix={
                searchFieldVariant === 'standard' ? undefined : (
                  <SearchOutlined
                    className="scope-input-suffix-action"
                    style={{
                      backgroundColor: 'var(--color-primary, #41aaaf)',
                      color: 'var(--color-on-primary, #052325)',
                      padding: 8,
                      borderRadius: 50,
                      fontSize: 16,
                      cursor: 'pointer',
                    }}
                    onClick={handleSearch}
                  />
                )
              }
            />
          </RightsToolbar>
          <RightsTable>
            {showPermissionsColumn ? (
            <RightsTableHeader
              $entityFirst={!showPermissionsColumn}
              $level={!showPermissionsColumn}
              $operations={Boolean(onLoadEntityOperations)}
            >
              <MobileGrantAllLabel>
                {t('users.access-rights-grant-all', {
                  defaultValue: 'Grant access to all',
                })}
              </MobileGrantAllLabel>
              <AccessCell className="user-rights-grant-all">
                <DrawerColumnHeader>{t('users.access')}</DrawerColumnHeader>
                <Space size={10} align="center">
                  <DrawerColumnHeader className="user-rights-grant-all__all-label">
                    {t('common.all')}
                  </DrawerColumnHeader>
                  <Tooltip
                    title={
                      selectAll
                        ? t('common.toggle-on-click-to-turn-off')
                        : t('common.toggle-off-click-to-turn-on')
                    }
                  >
                    <StyledSwitch
                      aria-label={`${t('common.all')} ${t('users.access')}`}
                      checked={selectAll}
                      onChange={onSelectAll}
                    />
                  </Tooltip>
                </Space>
              </AccessCell>
              <DrawerColumnHeader>{t('dashboard.entity-name')}</DrawerColumnHeader>
              <DrawerColumnHeader>
                {t('users.access-rights-permissions-column')}
              </DrawerColumnHeader>
            </RightsTableHeader>
            ) : (
            <RightsLevelHeader $operations={Boolean(onLoadEntityOperations)}>
              <DrawerColumnHeader className="level-header-entity">
                {t('dashboard.entity-name')}
              </DrawerColumnHeader>
              <LevelColumn className="level-header-title-refresh" $area="refresh">
                <LevelTitle>
                  <LevelTitleLines>
                    <span>
                      {t('users.access-level-refresh-line-1', { defaultValue: 'Refresh' })}
                    </span>
                    <span>
                      {t('users.access-level-refresh-line-2', { defaultValue: 'Only' })}
                    </span>
                  </LevelTitleLines>
                  <Tooltip
                    title={t('users.access-level-refresh-help', {
                      defaultValue:
                        'Refresh data for this entity. Read and write stay off.',
                    })}
                  >
                    <LevelHelpButton
                      type="button"
                      aria-label={t('users.access-level-refresh-help', {
                        defaultValue:
                          'Refresh data for this entity. Read and write stay off.',
                      })}
                    >
                      <QuestionMarkIcon />
                    </LevelHelpButton>
                  </Tooltip>
                </LevelTitle>
              </LevelColumn>
              <LevelOr className="level-header-or">
                <LevelOrLabel $placement="header">
                  {t('users.access-level-or', { defaultValue: '- OR -' })}
                </LevelOrLabel>
              </LevelOr>
              <LevelColumn className="level-header-title-read" $area="read">
                <LevelTitle>
                  <LevelTitleLines>
                    <span>{t('users.access-level-read-line-1', { defaultValue: 'GET' })}</span>
                    <span>
                      {t('users.access-level-read-line-2', { defaultValue: 'Full Read' })}
                    </span>
                  </LevelTitleLines>
                  <Tooltip
                    title={t('users.access-level-read-help', {
                      defaultValue: 'Read data from this entity. Refresh turns off.',
                    })}
                  >
                    <LevelHelpButton
                      type="button"
                      aria-label={t('users.access-level-read-help', {
                        defaultValue: 'Read data from this entity. Refresh turns off.',
                      })}
                    >
                      <QuestionMarkIcon />
                    </LevelHelpButton>
                  </Tooltip>
                </LevelTitle>
              </LevelColumn>
              <LevelColumn className="level-header-title-write" $area="write">
                <LevelTitle>
                  <LevelTitleLines>
                    <span>{t('users.access-level-write-line-1', { defaultValue: 'POST' })}</span>
                    <span>
                      {t('users.access-level-write-line-2', { defaultValue: 'Write' })}
                    </span>
                  </LevelTitleLines>
                  <Tooltip
                    title={t('users.access-level-write-help', {
                      defaultValue:
                        'Post data to this entity. Refresh turns off. With Full Read, access is full.',
                    })}
                  >
                    <LevelHelpButton
                      type="button"
                      aria-label={t('users.access-level-write-help', {
                        defaultValue:
                          'Post data to this entity. Refresh turns off. With Full Read, access is full.',
                      })}
                    >
                      <QuestionMarkIcon />
                    </LevelHelpButton>
                  </Tooltip>
                </LevelTitle>
              </LevelColumn>
              {onToggleAllAccess ? (
                <>
                  <LevelColumn className="level-header-switch-refresh" $area="refresh">
                    <StyledSwitch
                      size="small"
                      aria-label={t('users.access-level-refresh-all', {
                        defaultValue: 'Set Refresh Only for all entities',
                      })}
                      checked={allAccessOn('refresh')}
                      disabled={!rights?.length}
                      onChange={(checked) => toggleAllAccess('refresh', checked)}
                    />
                  </LevelColumn>
                  <LevelColumn className="level-header-switch-read" $area="read">
                    <StyledSwitch
                      size="small"
                      aria-label={t('users.access-level-read-all', {
                        defaultValue: 'Set Full Read for all entities',
                      })}
                      checked={allAccessOn('read')}
                      disabled={!rights?.length}
                      onChange={(checked) => toggleAllAccess('read', checked)}
                    />
                  </LevelColumn>
                  <LevelColumn className="level-header-switch-write" $area="write">
                    <StyledSwitch
                      size="small"
                      aria-label={t('users.access-level-write-all', {
                        defaultValue: 'Set Write for all entities',
                      })}
                      checked={allAccessOn('write')}
                      disabled={!rights?.length}
                      onChange={(checked) => toggleAllAccess('write', checked)}
                    />
                  </LevelColumn>
                </>
              ) : null}
            </RightsLevelHeader>
            )}
            <RightsScroller
              ref={scrollerRef}
              className={`scroller-rights${
                showPermissionsColumn ? '' : ' scroller-rights--levels'
              }`}
              tabIndex={0}
              role="region"
              aria-label={t('users.access-rights-permissions-column')}
            >
              {rightsList?.map((record) => {
                const hasAccess = Boolean(access[record.uuid]);
                const selectedIds = advancedRightsSelection[record.organisationId] || [];
                const isPlanLocked = !isAdvancedUserRights;
                const entityCell = (
                  <EntityNameCell $grow={!showPermissionsColumn}>
                    {record.providerName?.toLowerCase() === 'group' ? (
                      <UsergroupAddOutlined
                        style={{
                          fontSize: 28,
                          color: 'var(--color-on-logo-tile, #042a2a)',
                        }}
                      />
                    ) : (
                      renderProviderIcon?.(record.providerName) ?? (
                        <span style={{ fontSize: 12 }}>{record.providerName}</span>
                      )
                    )}
                    <EntityNameText ellipsis={{ tooltip: record.friendlyName }}>
                      {record.friendlyName || record.providerName}
                    </EntityNameText>
                  </EntityNameCell>
                );
                const accessCell = (
                  <AccessCell>
                      <AccessStatus $active={hasAccess}>
                        {hasAccess ? t('common.yes') : t('common.no')}
                      </AccessStatus>
                      <Tooltip
                        title={
                          hasAccess
                            ? t('common.toggle-on-click-to-turn-off')
                            : t('common.toggle-off-click-to-turn-on')
                        }
                      >
                        <StyledSwitch
                          aria-label={`${t('users.access')}: ${record.friendlyName}`}
                          checked={hasAccess}
                          onChange={(checked) => {
                            setAccess((prev) => ({ ...prev, [record.uuid]: checked }));
                            defer(() =>
                              saveUserRights(
                                record,
                                checked ? TablePermission.ALL : TablePermission.NONE,
                              ),
                            );
                            const allIds = advancedUserRights?.map((r) => r.uuid) || [];
                            setAdvancedRightsSelection((prev) => ({
                              ...prev,
                              [record.organisationId]: checked ? allIds : [],
                            }));
                            if (setAdvancedUserRight) {
                              defer(() =>
                                setAdvancedUserRight(
                                  record.accountUuid,
                                  record.organisationId,
                                  checked ? allIds : [],
                                ),
                              );
                            }
                          }}
                        />
                      </Tooltip>
                    </AccessCell>
                );
                const selection =
                  levels[record.uuid] ?? selectionFromPermission(record.tablePermission);
                const commitLevel = (next: LevelSelection) => {
                  setLevels((prev) => ({ ...prev, [record.uuid]: next }));
                  defer(() => saveUserRights(record, permissionFromSelection(next)));
                };
                const refreshLabel = t('users.access-level-refresh', {
                  defaultValue: 'Refresh Only',
                });
                const readLabel = t('users.access-level-read', {
                  defaultValue: 'Full Read / GET',
                });
                const writeLabel = t('users.access-level-write', {
                  defaultValue: 'Write / POST',
                });
                const refreshHelp = t('users.access-level-refresh-help', {
                  defaultValue: 'Refresh data for this entity. Read and write stay off.',
                });
                const readHelp = t('users.access-level-read-help', {
                  defaultValue: 'Read data from this entity. Refresh turns off.',
                });
                const writeHelp = t('users.access-level-write-help', {
                  defaultValue:
                    'Post data to this entity. Refresh turns off. With Full Read, access is full.',
                });
                return (
                  <RightsDataRow
                    key={record.uuid}
                    $entityFirst={!showPermissionsColumn}
                    $level={!showPermissionsColumn}
                    $operations={Boolean(onLoadEntityOperations)}
                  >
                    {showPermissionsColumn ? null : entityCell}
                    {showPermissionsColumn ? null : (
                      <>
                        <LevelColumn $area="refresh">
                          <LevelSwitchLabel>
                            {refreshLabel}
                            <Tooltip title={refreshHelp}>
                              <LevelHelpButton type="button" aria-label={refreshHelp}>
                                <QuestionMarkIcon />
                              </LevelHelpButton>
                            </Tooltip>
                          </LevelSwitchLabel>
                          <StyledSwitch
                            size="small"
                            aria-label={`${refreshLabel}: ${record.friendlyName}`}
                            checked={selection.refresh}
                            onChange={(checked) =>
                              commitLevel(
                                checked
                                  ? { refresh: true, read: false, write: false }
                                  : { ...selection, refresh: false },
                              )
                            }
                          />
                        </LevelColumn>
                        <LevelOr>
                          <LevelOrLabel $placement="row">
                            {t('users.access-level-or', { defaultValue: '- OR -' })}
                          </LevelOrLabel>
                        </LevelOr>
                        <LevelColumn $area="read">
                          <LevelSwitchLabel>
                            {readLabel}
                            <Tooltip title={readHelp}>
                              <LevelHelpButton type="button" aria-label={readHelp}>
                                <QuestionMarkIcon />
                              </LevelHelpButton>
                            </Tooltip>
                          </LevelSwitchLabel>
                          <StyledSwitch
                            size="small"
                            aria-label={`${readLabel}: ${record.friendlyName}`}
                            checked={selection.read}
                            onChange={(checked) =>
                              commitLevel({
                                refresh: false,
                                read: checked,
                                write: selection.write,
                              })
                            }
                          />
                        </LevelColumn>
                        <LevelColumn $area="write">
                          <LevelSwitchLabel>
                            {writeLabel}
                            <Tooltip title={writeHelp}>
                              <LevelHelpButton type="button" aria-label={writeHelp}>
                                <QuestionMarkIcon />
                              </LevelHelpButton>
                            </Tooltip>
                          </LevelSwitchLabel>
                          <StyledSwitch
                            size="small"
                            aria-label={`${writeLabel}: ${record.friendlyName}`}
                            checked={selection.write}
                            onChange={(checked) =>
                              commitLevel({
                                refresh: false,
                                read: selection.read,
                                write: checked,
                              })
                            }
                          />
                        </LevelColumn>
                        {onLoadEntityOperations ? (
                          <OperationsTestCell>
                            <ScopeButton
                              type="default"
                              width="100%"
                              height="32px"
                              fontSize="11px"
                              lineheight="12px"
                              disabled={!selection.read && !selection.write}
                              aria-label={`${t('users.access-rights-load-operations', {
                                defaultValue: 'Limit access rights',
                              })}: ${record.friendlyName}`}
                              onClick={() =>
                                onLoadEntityOperations(
                                  record.accountUuid,
                                  record.organisationId,
                                )
                              }
                            >
                              {t('users.access-rights-load-operations', {
                                defaultValue: 'Limit access rights',
                              })}
                            </ScopeButton>
                          </OperationsTestCell>
                        ) : null}
                      </>
                    )}
                    {showPermissionsColumn ? accessCell : null}
                    {showPermissionsColumn ? entityCell : null}
                    {!showPermissionsColumn ? null : !hasAccess ? (
                      <PermissionsPanel $compactEmpty>
                        <Typography.Text
                          style={{ fontSize: 13, color: 'var(--color-text-muted, #5a6a6a)' }}
                        >
                          {t('users.permissions-none')}
                        </Typography.Text>
                      </PermissionsPanel>
                    ) : isPlanLocked ? (
                      <>
                        <LockedPermissionsHint
                          type="button"
                          onClick={onUpgradeClick}
                          disabled={!onUpgradeClick}
                          style={{
                            cursor: onUpgradeClick ? 'pointer' : 'default',
                          }}
                        >
                          <LockOutlined style={{ fontSize: 14 }} aria-hidden />
                          {t('users.permissions-all')}
                        </LockedPermissionsHint>
                        <LockedDesktopPanel
                          onClick={onUpgradeClick}
                          style={{ cursor: onUpgradeClick ? 'pointer' : 'default' }}
                        >
                          <PermissionChipGrid>
                            <ColoredTag
                              bgcolor="var(--color-status-success-bg, #E9F6F7)"
                              bordercolor="var(--color-border, #DBEAE3)"
                            >
                              {t('users.permissions-all')}
                            </ColoredTag>
                            <LockOutlined
                              style={{
                                color: 'var(--color-text-muted, #5a6a6a)',
                                fontSize: 16,
                              }}
                            />
                          </PermissionChipGrid>
                        </LockedDesktopPanel>
                      </>
                    ) : sortedAdvancedRights.length === 0 ? (
                      <PermissionsPanel $compactEmpty>
                        <Typography.Text
                          style={{ fontSize: 13, color: 'var(--color-text-muted, #5a6a6a)' }}
                        >
                          {t('users.permissions-select-hint')}
                        </Typography.Text>
                      </PermissionsPanel>
                    ) : (
                      <PermissionsPanel>
                        <PermissionChipGrid>
                          {sortedAdvancedRights.map((right) => {
                            const isSelected = selectedIds.includes(right.uuid);
                            return (
                              <Tooltip key={right.uuid} title={right.description}>
                                <PermissionChip
                                  type="button"
                                  $selected={isSelected}
                                  aria-pressed={isSelected}
                                  onClick={() => {
                                    const current =
                                      advancedRightsSelection[record.organisationId] || [];
                                    const next = isSelected
                                      ? current.filter((id) => id !== right.uuid)
                                      : [...current, right.uuid];
                                    handleAdvancedRightsChange(
                                      record.accountUuid,
                                      record.organisationId,
                                      next,
                                    );
                                  }}
                                >
                                  {operationChipLabel(right, t)}
                                  <QuestionMarkIcon />
                                </PermissionChip>
                              </Tooltip>
                            );
                          })}
                        </PermissionChipGrid>
                        {selectedIds.length > 0 && activeEntityCount > 1 && (
                          <PermissionsFooter>
                            <ScopeButton
                              type="default"
                              width="auto"
                              height="28px"
                              fontSize="12px"
                              lineheight="12px"
                              onClick={() =>
                                setApplyToAllOrganisationId(record.organisationId)
                              }
                            >
                              {t('users.permissions-apply-to-all')}
                            </ScopeButton>
                          </PermissionsFooter>
                        )}
                      </PermissionsPanel>
                    )}
                  </RightsDataRow>
                );
              })}
            </RightsScroller>
          </RightsTable>
        </RightsContent>
      </RightsSpin>
      <Modal
        open={applyToAllOrganisationId != null}
        title={t('users.permissions-apply-to-all-modal-title')}
        onCancel={() => setApplyToAllOrganisationId(null)}
        onOk={() => {
          if (applyToAllOrganisationId) {
            handleApplyToAll(applyToAllOrganisationId);
            setApplyToAllOrganisationId(null);
          }
        }}
        okText={t('common.apply')}
        cancelText={t('common.cancel')}
      >
        <p>
          {t('users.permissions-apply-to-all-modal-description', {
            entityName: applyToAllSourceName,
            count: activeEntityCount,
          })}
        </p>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--color-text-muted, #5a6a6a)' }}>
          {t('users.permissions-apply-to-all-modal-hint')}
        </p>
      </Modal>
    </>
  );
};
